/**
 * ============================================================================
 * DOCUMENTS STORE - DELETE OPERATIONS
 * ============================================================================
 * 
 * Handles deleting documents via the backend API.
 * 
 * Features:
 * - Optimistic deletion for instant UI feedback
 * - Automatic rollback on failure
 * 
 * @module documents/api/delete
 */

import { apiService } from "@/lib/api-functions/api-service";
import { documentStore } from "../store";
import type { ToastFunction } from "../types";

// ============================================================================
// DELETE OPERATIONS
// ============================================================================

/**
 * Delete a document with local-first approach.
 * 
 * Optimistically removes from store, then confirms with server.
 * Automatically rolls back on failure.
 * 
 * @param id - The document ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for notifications
 * 
 * @example
 * ```ts
 * await deleteDocumentItem('doc-123', 'game-456', toast);
 * ```
 */
export async function deleteDocumentItem(
  id: string,
  gameId: string,
  toast?: ToastFunction
): Promise<void> {
  // Store the current state for potential rollback
  const currentData = documentStore.getData();
  
  // ========================================================================
  // OPTIMISTIC DELETE
  // ========================================================================
  const optimisticData = currentData.filter(doc => doc.id !== id);
  documentStore.setData(optimisticData);

  try {
    console.log('[deleteDocumentItem] Deleting:', { id, gameId });

    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // DELETE VIA API
    // ======================================================================
    const result = await apiService.deleteDocumentItem(id, gameId);

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete document');
    }

    console.log(`[deleteDocumentItem] Deleted document: ${id}`);
    
  } catch (err) {
    // ======================================================================
    // ROLLBACK ON FAILURE
    // ======================================================================
    documentStore.setData(currentData);
    
    const message = err instanceof Error ? err.message : 'Failed to delete document';
    console.error('[deleteDocumentItem] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
